import type { PaymentMethod, Stripe, StripeElements } from '@stripe/stripe-js'
import { updateStripeOffSessionCardByRegionAndPurposeId, type LuxPlusOffSessionPaymentCard } from 'api/payment'
import config from 'constants/config'

export interface LuxPlusCheckoutCardBackupCandidate {
  paymentMethodId?: string
  fingerprint?: string
  brand?: string
  last4?: string
  funding?: string
  expMonth?: number
  expYear?: number
  walletType?: string
  requiresFingerprintMatch?: boolean
}

interface PrepareLuxPlusCheckoutCardBackupCandidateInput {
  account: Pick<App.AuthAccount, 'email' | 'fullName'>
  elements: StripeElements
  stripe: Stripe
}

interface AppendLuxPlusBackupCardInput {
  currencyCode: string
  elements?: StripeElements
  paymentMethodId: string
  purposeId: string
  regionCode: string
  stripe: Stripe
}

export interface LuxPlusCheckoutCardBackupEligibilityInput {
  shouldShowBackupPaymentMethod: boolean
  hasActiveLuxPlusSubscription: boolean
  hasExistingLuxPlusBackupPaymentMethod?: boolean
  hasLuxPlusSubscriptionInCart: boolean
  isCardPaymentMethod: boolean
  selectedCheckoutCard?: LuxPlusCheckoutCardBackupCandidate
  currentLuxPlusPaymentMethod?: LuxPlusCheckoutCardBackupCandidate
}

export function stripePaymentCardToLuxPlusCheckoutCardBackupCandidate(card: App.StripePaymentCardV2): LuxPlusCheckoutCardBackupCandidate {
  return {
    paymentMethodId: card.paymentMethodId,
    fingerprint: card.fingerprint,
    brand: card.brand,
    last4: card.last4,
    funding: card.funding,
    expMonth: card.expMonth,
    expYear: card.expYear,
  }
}

export function luxPlusOffSessionCardToLuxPlusCheckoutCardBackupCandidate(card: LuxPlusOffSessionPaymentCard): LuxPlusCheckoutCardBackupCandidate {
  return {
    paymentMethodId: card.paymentMethod,
    fingerprint: card.card?.fingerprint,
    brand: card.card?.brand,
    last4: card.card?.last4,
    funding: card.card?.funding,
    expMonth: card.card?.expMonth,
    expYear: card.card?.expYear,
  }
}

export function stripePaymentMethodToLuxPlusCheckoutCardBackupCandidate(paymentMethod: PaymentMethod): LuxPlusCheckoutCardBackupCandidate {
  if (paymentMethod.type !== 'card') return {}

  return {
    paymentMethodId: paymentMethod.id,
    fingerprint: paymentMethod.card?.fingerprint ?? undefined,
    brand: paymentMethod.card?.brand ?? undefined,
    last4: paymentMethod.card?.last4 ?? undefined,
    funding: paymentMethod.card?.funding ?? undefined,
    expMonth: paymentMethod.card?.exp_month ?? undefined,
    expYear: paymentMethod.card?.exp_year ?? undefined,
    walletType: paymentMethod.card?.wallet?.type ?? undefined,
  }
}

export async function prepareLuxPlusCheckoutCardBackupCandidate(input: PrepareLuxPlusCheckoutCardBackupCandidateInput): Promise<LuxPlusCheckoutCardBackupCandidate> {
  const { account, elements, stripe } = input
  const { error: submitError } = await elements.submit()

  if (submitError) {
    throw new Error(submitError.message)
  }

  const { error, paymentMethod } = await stripe.createPaymentMethod({
    elements,
    params: {
      billing_details: {
        name: account.fullName,
        email: account.email,
      },
    },
  })

  if (error) {
    throw new Error(error.message)
  }

  return stripePaymentMethodToLuxPlusCheckoutCardBackupCandidate(paymentMethod)
}

export async function appendLuxPlusBackupCard(input: AppendLuxPlusBackupCardInput): Promise<void> {
  const {
    currencyCode,
    elements,
    paymentMethodId,
    purposeId,
    regionCode,
    stripe,
  } = input

  const { clientSecret } = await updateStripeOffSessionCardByRegionAndPurposeId(regionCode, purposeId, {
    currency: currencyCode,
    brand: config.BRAND,
    paymentMethodType: 'card',
    metadata: {
      paymentMethodId,
      purpose: 'lux_plus',
      shouldAppendBackupOffSessionCard: true,
    },
  })

  const setupResult = elements ?
      await stripe.confirmSetup({
        clientSecret,
        redirect: 'if_required',
      }) :
      await stripe.confirmCardSetup(clientSecret)

  if (setupResult.error) {
    throw new Error(setupResult.error.message)
  }
}

export function hasMatchingLuxPlusCheckoutCardBackupCandidate(
  checkoutCard: LuxPlusCheckoutCardBackupCandidate,
  luxPlusPaymentMethod?: LuxPlusCheckoutCardBackupCandidate,
) {
  if (!luxPlusPaymentMethod) return false

  return Boolean(
    (checkoutCard.paymentMethodId && luxPlusPaymentMethod.paymentMethodId && checkoutCard.paymentMethodId === luxPlusPaymentMethod.paymentMethodId) ||
    hasMatchingCardFingerprintAndExpiry(checkoutCard, luxPlusPaymentMethod) ||
    (!checkoutCard.fingerprint && hasMatchingCardDetails(checkoutCard, luxPlusPaymentMethod)),
  )
}

function hasMatchingCardFingerprintAndExpiry(
  checkoutCard: LuxPlusCheckoutCardBackupCandidate,
  luxPlusPaymentMethod: LuxPlusCheckoutCardBackupCandidate,
) {
  return Boolean(
    checkoutCard.fingerprint &&
    luxPlusPaymentMethod.fingerprint &&
    checkoutCard.expMonth &&
    checkoutCard.expYear &&
    luxPlusPaymentMethod.expMonth &&
    luxPlusPaymentMethod.expYear &&
    checkoutCard.fingerprint === luxPlusPaymentMethod.fingerprint &&
    checkoutCard.expMonth === luxPlusPaymentMethod.expMonth &&
    checkoutCard.expYear === luxPlusPaymentMethod.expYear,
  )
}

function hasCompleteCardDetails(card: LuxPlusCheckoutCardBackupCandidate) {
  return Boolean(
    card.brand &&
    card.last4 &&
    card.funding &&
    card.expMonth &&
    card.expYear,
  )
}

function hasMatchingCardDetails(
  checkoutCard: LuxPlusCheckoutCardBackupCandidate,
  luxPlusPaymentMethod: LuxPlusCheckoutCardBackupCandidate,
) {
  return Boolean(
    hasCompleteCardDetails(checkoutCard) &&
    hasCompleteCardDetails(luxPlusPaymentMethod) &&
    checkoutCard.brand === luxPlusPaymentMethod.brand &&
    checkoutCard.last4 === luxPlusPaymentMethod.last4 &&
    checkoutCard.funding === luxPlusPaymentMethod.funding &&
    checkoutCard.expMonth === luxPlusPaymentMethod.expMonth &&
    checkoutCard.expYear === luxPlusPaymentMethod.expYear,
  )
}

export function shouldShowLuxPlusCheckoutCardBackupCheckbox(input: LuxPlusCheckoutCardBackupEligibilityInput) {
  const {
    shouldShowBackupPaymentMethod,
    hasActiveLuxPlusSubscription,
    hasExistingLuxPlusBackupPaymentMethod,
    hasLuxPlusSubscriptionInCart,
    isCardPaymentMethod,
    selectedCheckoutCard,
    currentLuxPlusPaymentMethod,
  } = input

  if (!shouldShowBackupPaymentMethod) return false
  if (!hasActiveLuxPlusSubscription) return false
  if (hasExistingLuxPlusBackupPaymentMethod) return false
  if (hasLuxPlusSubscriptionInCart) return false
  if (!isCardPaymentMethod) return false
  if (!selectedCheckoutCard?.paymentMethodId && !selectedCheckoutCard?.fingerprint) return false
  if (selectedCheckoutCard.requiresFingerprintMatch && !selectedCheckoutCard.fingerprint && !hasCompleteCardDetails(selectedCheckoutCard)) return false

  return !hasMatchingLuxPlusCheckoutCardBackupCandidate(selectedCheckoutCard, currentLuxPlusPaymentMethod)
}
